import { Journey } from '@audibene/ta.utils-opportunity'
import { useQuery } from 'react-query'
import { AppointmentType } from '../../constants'
import { GetAvailabilitiesRequest } from 'hooks/services/appointments/availabilities'
import { logFactory } from '../../util'
import { useAppointmentsService } from '../services'
import { useAvailabilityCacheEnabled } from '../useAvailabilityCacheEnabled'

const hookName = 'useAsegoAvailabilityTimes'
const log = logFactory(hookName)

interface Props {
  appointmentType: AppointmentType
  dateEnd: Date
  dateStart: Date
  durationInMinutes?: number
  isEnabled?: boolean
  journey: Journey
  resourceGids?: string[]
  timeZone: string
}

const useAsegoAvailabilityTimes = ({
  appointmentType,
  dateEnd: date_end,
  dateStart: date_start,
  durationInMinutes,
  isEnabled = true,
  journey,
  resourceGids,
  timeZone,
}: Props) => {
  const { getAsegoTimes, isReady } = useAppointmentsService()
  const shouldUseCache = useAvailabilityCacheEnabled()

  const params: GetAvailabilitiesRequest = {
    allow_integration_errors: true,
    appointment_type: appointmentType,
    customer_timezone: timeZone,
    date_start,
    date_end,
    duration_in_minutes: durationInMinutes,
    journey,
    resource_gids: resourceGids,
    use_cache: shouldUseCache,
  }

  const fetchAsegoTimes = async () => {
    log(`⏰ Fetching Asego availability times for date ${date_start.toISOString()}...`, params)

    const timeStart = Date.now()
    const times = await getAsegoTimes(params)
    const timeEnd = Date.now()
    const timeInMs = timeEnd - timeStart

    if (times.responseStatus === 200) {
      log(`⏰ Found Asego availability times (${timeInMs}ms)`, times)
    } else {
      log(`⚠️ Failed to fetch Asego availability times (${timeInMs}ms)`, {
        status: times.responseStatus,
      })
    }

    return times
  }

  const isQueryEnabled = Boolean(isReady && isEnabled && date_start)
  const results = useQuery([hookName, params], fetchAsegoTimes, {
    enabled: isQueryEnabled,
  })

  const { isLoading, data } = results

  return {
    ...results,
    data,
    isLoading,
  }
}

export { hookName as useAsegoAvailabilityTimesHookName, useAsegoAvailabilityTimes }
